/**
 * Hook for collection monitoring data
 *
 * Fetches case-wide collection monitoring data with summary statistics
 * and supports filtering by simplified status, collection request, and hostname.
 */

import { useState, useEffect, useCallback } from 'react'
import type {
  MonitoringItem,
  MonitoringSummary,
  MonitoringFilterParams,
} from '../types/collection-targets'
import { getCollectionMonitoring } from '../services/collection-targets'

// =============================================================================
// useCollectionMonitoring
// =============================================================================

interface UseCollectionMonitoringResult {
  items: MonitoringItem[]
  summary: MonitoringSummary | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

const EMPTY_SUMMARY: MonitoringSummary = {
  totalTargets: 0,
  requestedCount: 0,
  returnedCount: 0,
  issueCount: 0,
  recollectionCount: 0,
  uniqueEndpoints: 0,
}

/**
 * Hook to fetch collection monitoring data for a case
 */
export function useCollectionMonitoring(
  caseId: string | null,
  params?: MonitoringFilterParams
): UseCollectionMonitoringResult {
  const [items, setItems] = useState<MonitoringItem[]>([])
  const [summary, setSummary] = useState<MonitoringSummary | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!caseId) {
      setItems([])
      setSummary(null)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await getCollectionMonitoring(caseId, params)
      setItems(response.items)
      setSummary(response.summary)
    } catch (error_) {
      setError(
        error_ instanceof Error ? error_.message : 'Failed to fetch collection monitoring data'
      )
      setItems([])
      setSummary(EMPTY_SUMMARY)
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable individual param values
  }, [caseId, params?.status, params?.requestId, params?.hostname])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { items, summary, loading, error, refetch: fetch }
}
